/**
 * PrototypeService
 * Handles all database operations for prototype management
 */

import { supabase } from '@xanectech/supabase-client';
import type {
  Prototype,
  CreatePrototypeData,
  UpdatePrototypeData,
  PrototypeStatus,
} from '@xanectech/types';
import type { Json } from '@xanectech/supabase-client';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - PrototypeService');

interface PrototypeUpdatePayload {
  description?: string | null;
  metadata?: Json;
  name?: string;
  route_path?: string;
  status?: PrototypeStatus;
}

export class PrototypeService {
  /**
   * Get all prototypes
   */
  static async getAllPrototypes(): Promise<Prototype[]> {
    const { data, error } = await supabase
      .from('prototypes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to fetch prototypes:', error);
      throw new Error('Unable to fetch prototypes');
    }

    return (data || []).map((row) => ({
      ...row,
      metadata: (row.metadata as Record<string, unknown>) || {},
      status: row.status as PrototypeStatus,
    }));
  }

  /**
   * Get prototype by ID
   */
  static async getPrototypeById(id: string): Promise<Prototype> {
    const { data, error } = await supabase
      .from('prototypes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      logger.error('Failed to fetch prototype:', error);
      throw new Error('Unable to fetch prototype');
    }

    return {
      ...data,
      metadata: (data.metadata as Record<string, unknown>) || {},
      status: data.status as PrototypeStatus,
    };
  }

  /**
   * Create new prototype
   */
  static async createPrototype(
    prototypeData: CreatePrototypeData,
  ): Promise<Prototype> {
    const { data, error } = await supabase
      .from('prototypes')
      .insert({
        name: prototypeData.name,
        description: prototypeData.description || null,
        route_path: prototypeData.route_path,
        status: prototypeData.status || 'in-development',
        metadata: (prototypeData.metadata || {}) as Json,
      })
      .select()
      .single();

    if (error) {
      logger.error('Failed to create prototype:', error);
      throw new Error('Unable to create prototype');
    }

    return {
      ...data,
      metadata: (data.metadata as Record<string, unknown>) || {},
      status: data.status as PrototypeStatus,
    };
  }

  /**
   * Update prototype
   */
  static async updatePrototype(
    id: string,
    updates: UpdatePrototypeData,
  ): Promise<Prototype> {
    const updateData: PrototypeUpdatePayload = {};

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined)
      updateData.description = updates.description;
    if (updates.route_path !== undefined)
      updateData.route_path = updates.route_path;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.metadata !== undefined)
      updateData.metadata = updates.metadata as Json;

    const { data, error } = await supabase
      .from('prototypes')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      logger.error('Failed to update prototype:', error);
      throw new Error('Unable to update prototype');
    }

    return {
      ...data,
      metadata: (data.metadata as Record<string, unknown>) || {},
      status: data.status as PrototypeStatus,
    };
  }

  /**
   * Delete prototype
   */
  static async deletePrototype(id: string): Promise<void> {
    const { error } = await supabase.from('prototypes').delete().eq('id', id);

    if (error) {
      logger.error('Failed to delete prototype:', error);
      throw new Error('Unable to delete prototype');
    }
  }

  /**
   * Update prototype status
   */
  static async updatePrototypeStatus(
    id: string,
    status: 'in-development' | 'active' | 'archived',
  ): Promise<Prototype> {
    return this.updatePrototype(id, { status });
  }
}
